import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import api from "../../../config/URL";
import { toast } from "react-toastify";

function StudentWithdrawAdd() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const studentId = queryParams.get("studentId");
  const classId = queryParams.get("classId");
  const centerId = queryParams.get("centerId");

  const [loadIndicator, setLoadIndicator] = useState(false);
  const [isLoading, setIsLoading] = useState(true); // Track initial data loading
  const [lastLessonDateData, setLastLessonDateData] = useState([]);
  const [studentWithdrawData, setStudentWithdrawData] = useState(null);
  const [depositRefundAllowed, setDepositRefund] = useState(false);
  const [depositForfeitAllowed, setDepositForfeit] = useState(false);
  const [courseFeesAmount, setCourseFeesAmount] = useState(0);
  const [registrationFeesRefund, setRegistrationFeesRefund] = useState(0);

  const validationSchema = Yup.object({
    studentId: Yup.string().required("Student Name is required"),
    course: Yup.string().required("Course is required"),
    teacher: Yup.string().required("Teacher is required"),
    lastClass: Yup.string().required("Last Class is required"),
    dayTiming: Yup.string().required("Day Timing is required"),
    classStartDate: Yup.string().required("Class Start Date is required"),
    classEndDate: Yup.string().required("Class End Date is required"),
    requestDate: Yup.date().required("Request Date is required").nullable(),
    withdrawReason: Yup.string().required("Withdraw Reason is required"),
    remarkCenter: Yup.string().required("Remark Centre is required"),
    lastLessonDate: Yup.string().required("Last Lesson Date is required"),
    registrationFees: Yup.number()
      .required("Registration Fees is required")
      .min(0, "Registration Fees cannot be negative"),
    depositAmount: Yup.number()
      .required("Deposit Amount is required")
      .min(0, "Deposit Amount cannot be negative"),
    depositOffsetAmount: Yup.number()
      .required("Deposit Offset Amount is required")
      .min(0, "Deposit Offset Amount cannot be negative"),
    noOfLessonToRefund: Yup.number()
      .required("Number of Lessons to Refund is required")
      .min(0, "Number of Lessons cannot be negative")
      .integer("Number of Lessons must be an integer"),
    courseFeesAmount: Yup.number()
      .required("Course Fees Amount is required")
      .min(0, "Course Fees Amount cannot be negative"),
    materialFeesAmount: Yup.number()
      .required("Material Fees Amount is required")
      .min(0, "Material Fees Amount cannot be negative"),
    totalRefundableAmount: Yup.number()
      .required("Total Refundable Amount is required")
      .min(0, "Total Refundable Amount cannot be negative"),
  });

  const formik = useFormik({
    initialValues: {
      studentId: studentId || "",
      courseId: "",
      course: "",
      userId: "",
      teacher: "",
      lastClass: "",
      classId: "",
      dayTiming: "",
      classStartDate: "",
      classEndDate: "",
      requestDate: "",
      withdrawReason: "",
      remarkParent: "",
      remarkCenter: "",
      lastLessonDate: "",
      lastPaidLessonDate: "",
      noOfLessonNotice: 0,
      noOfLessonNoticeForRefund: 4,
      expectedRefundDate: "",
      forfeitAmount: "",
      refundName: "",
      refundAddress: "",
      refundMode: "",
      refundAmount: "",
      chequeNo: "",
      chequeDate: "",
      chequeAmount: "",
      bankName: "",
      bankAccountName: "",
      bankAccountNumber: "",
      registrationFees: 0,
      depositAmount: 0,
      depositOffsetAmount: 0,
      noOfLessonToRefund: 0,
      courseFeesAmount: 0,
      materialFeesAmount: 0,
      totalRefundableAmount: 0,
      depositRefundAllowed: false,
      depositForfeitAllowed: false,
      courseFeesRefund: false,
      registrationFeesRefundAllowed: false,
    },
    validationSchema,
    enableReinitialize: false, // Disable reinitialization to prevent resetting
    onSubmit: async (values) => {
      setLoadIndicator(true);
      try {
        const response = await api.post("/createStudentWithdrawClass", values, {
          headers: { "Content-Type": "application/json" },
        });
        if (response.status === 201) {
          toast.success(response.data.message);
          navigate("/withdraw");
        } else {
          toast.error(response.data.message);
        }
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to create");
      } finally {
        setLoadIndicator(false);
      }
    },
    validateOnChange: false,
    validateOnBlur: true,
  });

  // Fetch all initial data in a single useEffect
  useEffect(() => {
    const fetchInitialData = async () => {
      if (!studentId || !classId || !centerId) {
        toast.error("Missing required query parameters");
        setIsLoading(false);
        return;
      }
      try {
        setIsLoading(true);
        // Fetch all data concurrently
        const [withdrawResponse, registrationResponse] =
          await Promise.all([
            api.get(
              `getPreviousStudentClassDetails?classId=${classId}&studentId=${studentId}`
            ),
            api.get(`getLatestCenterRegistrationByCenterId/${centerId}`),
          ]);

        // Process withdraw data
        const withdrawData = withdrawResponse.data;
        setStudentWithdrawData(withdrawData);

        // Process last lesson data
        // const lessonData = lessonResponse.data || "";
        // formik.setFieldValue("noOfLessonNotice", lessonData.remainingLessons || 0);
        // formik.setFieldValue(
        //   "noOfLessonNoticeForRefund",
        //   lessonData.noOfLessonNoticeForRefund || 4
        // );

        // Process registration fees
        const registrationData = registrationResponse.data;
        setRegistrationFeesRefund(registrationData?.amount || 0);
        formik.setFieldValue("registrationFees", registrationData?.amount || 0);

        // Set form values
        formik.setValues({
          ...formik.values,
          studentId,
          courseId: withdrawData.courseId || "",
          course: withdrawData.courseName || "",
          userId: withdrawData.teacherId || "",
          teacher: withdrawData.teacherName || "",
          lastClass: withdrawData.className || "",
          classId: withdrawData.classId || "",
          dayTiming: withdrawData.dayTiming || "",
          classStartDate: withdrawData.classStartDate || "",
          classEndDate: withdrawData.classEndDate || "",
          lastPaidLessonDate: withdrawData.lastPaidLessonDate?.substring(0, 10) || "",
        });
      } catch (error) {
        toast.error(error.message || "Failed to fetch initial data");
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, [studentId, classId, centerId]);

  // Fetch course fees refund when lastLessonDate changes
  useEffect(() => {
    const fetchCourseFeesRefund = async () => {
      if (!formik.values.lastLessonDate || !studentId || !classId) return;

      try {
        const response = await api.get(
          `getCalculateCourseFeesRefund?classId=${classId}&studentId=${studentId}&lastLessonDate=${formik.values.lastLessonDate}`
        );
        const calculatedFee = response.data?.calculatedFee || 0;
        setCourseFeesAmount(calculatedFee);
        formik.setFieldValue("courseFeesAmount", calculatedFee);
        formik.setFieldValue("courseFeesRefund", true);
      } catch (error) {
        toast.error(error.message || "Failed to fetch course fees refund");
        formik.setFieldValue("courseFeesRefund", false);
      }
    };

    fetchCourseFeesRefund();
  }, [formik.values.lastLessonDate, studentId, classId]);

  useEffect(() => {
    const fetchLastLessonDate = async () => {
      try {
        const response = await api.get(
          `getLastLessonClassDate?classId=${12}&studentId=${74}`
        );
        const lastLessonDate = response.data.startDate;
        setLastLessonDateData(lastLessonDate);
      } catch (error) {
        toast.error(error.message || "Failed to fetch");
      }
    };
  
    // Set timeout for 1 second (1000 ms)
    const timeoutId = setTimeout(() => {
      fetchLastLessonDate();
    }, 1000);
  
    // Cleanup timeout on component unmount or dependency change
    return () => clearTimeout(timeoutId);
  }, [studentId, classId]);
  

  // Calculate total refundable amount
  useEffect(() => {
    const calculateTotalRefundable = () => {
      const {
        registrationFees,
        depositAmount,
        depositOffsetAmount,
        noOfLessonToRefund,
        courseFeesAmount,
        materialFeesAmount,
        registrationFeesRefundAllowed,
        depositRefundAllowed,
        courseFeesRefund,
      } = formik.values;

      let total = 0;
      if (registrationFeesRefundAllowed) total += parseFloat(registrationFees) || 0;
      if (depositRefundAllowed) total += parseFloat(depositAmount) || 0;
      total += parseFloat(depositOffsetAmount) || 0;
      if (courseFeesRefund) total += parseFloat(courseFeesAmount) || 0;
      total += parseFloat(materialFeesAmount) || 0;
      total += parseFloat(noOfLessonToRefund) || 0;

      formik.setFieldValue("totalRefundableAmount", total.toFixed(2));
    };

    calculateTotalRefundable();
  }, [
    formik.values.registrationFees,
    formik.values.depositAmount,
    formik.values.depositOffsetAmount,
    formik.values.noOfLessonToRefund,
    formik.values.courseFeesAmount,
    formik.values.materialFeesAmount,
    formik.values.registrationFeesRefundAllowed,
    formik.values.depositRefundAllowed,
    formik.values.courseFeesRefund,
  ]);

  // Handle last lesson date change
  const handleLastLessonDateChange = (e) => {
    const selectedValue = e.target.value;
    formik.setFieldValue("lastLessonDate", selectedValue);
    if (selectedValue) {
      formik.setFieldValue("courseFeesRefund", true);
    } else {
      formik.setFieldValue("courseFeesRefund", false);
      formik.setFieldValue("courseFeesAmount", 0);
      setCourseFeesAmount(0);
    }
  };

  // Scroll to first error on submit
  useEffect(() => {
    if (formik.submitCount > 0 && Object.keys(formik.errors).length > 0) {
      const errorField = Object.keys(formik.errors)[0];
      const errorElement = document.querySelector(`[name="${errorField}"]`);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
        errorElement.focus();
      }
    }
  }, [formik.submitCount, formik.errors]);

  if (isLoading) {
    return (
      <div className="container-fluid">
        <div className="loader-container">
            <div className="loading">
              <span></span>
              <span></span>
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
      </div>
    );
  }
  return (
    <div className="container-fluid">
      <ol
        className="breadcrumb my-3 px-2"
        style={{ listStyle: "none", padding: 0, margin: 0 }}
      >
        <li>
          <Link to="/" className="custom-breadcrumb">
            Home
          </Link>
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li>
          &nbsp; Student Management
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li className="breadcrumb-item active" aria-current="page">
          &nbsp;Withdraw Add
        </li>
      </ol>
      <form onSubmit={formik.handleSubmit}>
        <div className="card">
          <div
            className="d-flex justify-content-between align-items-center p-1 mb-4 px-4"
            style={{ background: "#f5f7f9" }}
          >
            <div class="d-flex align-items-center">
              <div class="d-flex">
                <div class="dot active"></div>
              </div>
              <span class="me-2 text-muted">Add Withdraw</span>
            </div>
            <div className="my-2 pe-3 d-flex align-items-center">
              <Link to="/withdraw">
                <button type="button " className="btn btn-sm btn-border">
                  Back
                </button>
              </Link>
              &nbsp;&nbsp;
              <button
                type="submit"
                className="btn btn-button btn-sm"
                disabled={loadIndicator}
              >
                {loadIndicator && (
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    aria-hidden="true"
                  ></span>
                )}
                <span className="fw-medium">Save</span>
              </button>
            </div>
          </div>
          <div className="container-fluid px-4">
            <div className="row">
              <div className="col-md-6 col-12 mb-2">
                <div className="mb-3">
                  <label className="form-label">
                    Course<span className="text-danger">*</span>
                  </label>
                  <input
                    name="course"
                    {...formik.getFieldProps("course")}
                    type="text"
                    className={`form-control  ${
                      formik.touched.course && formik.errors.course
                        ? "is-invalid"
                        : ""
                    }`}
                    readOnly
                  />
                  {formik.touched.course && formik.errors.course && (
                    <div className="invalid-feedback">
                      {formik.errors.course}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-md-6 col-12 mb-2">
                <div className="mb-3">
                  <label className="form-label">
                    Teacher<span className="text-danger">*</span>
                  </label>
                  <input
                    name="teacher"
                    {...formik.getFieldProps("teacher")}
                    type="text"
                    className={`form-control  ${
                      formik.touched.teacher && formik.errors.teacher
                        ? "is-invalid"
                        : ""
                    }`}
                    readOnly
                  />
                  {formik.touched.teacher && formik.errors.teacher && (
                    <div className="invalid-feedback">
                      {formik.errors.teacher}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-md-6 col-12 mb-2">
                <div className="mb-3">
                  <label className="form-label">
                    Last Class<span className="text-danger">*</span>
                  </label>
                  <input
                    name="lastClass"
                    {...formik.getFieldProps("lastClass")}
                    type="text"
                    className={`form-control  ${
                      formik.touched.lastClass && formik.errors.lastClass
                        ? "is-invalid"
                        : ""
                    }`}
                    readOnly
                  />
                  {formik.touched.lastClass && formik.errors.lastClass && (
                    <div className="invalid-feedback">
                      {formik.errors.lastClass}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-md-6 col-12 mb-2">
                <div className="mb-3">
                  <label className="form-label">
                    Day timing<span className="text-danger">*</span>
                  </label>
                  <input
                    name="dayTiming"
                    {...formik.getFieldProps("dayTiming")}
                    type="text"
                    className={`form-control  ${
                      formik.touched.dayTiming && formik.errors.dayTiming
                        ? "is-invalid"
                        : ""
                    }`}
                    readOnly
                  />
                  {formik.touched.dayTiming && formik.errors.dayTiming && (
                    <div className="invalid-feedback">
                      {formik.errors.dayTiming}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-md-6 col-12 mb-2">
                <div className="mb-3">
                  <label className="form-label">
                    Class Start date<span className="text-danger">*</span>
                  </label>
                  <input
                    name="classStartDate"
                    {...formik.getFieldProps("classStartDate")}
                    type="text"
                    className={`form-control  ${
                      formik.touched.classStartDate &&
                      formik.errors.classStartDate
                        ? "is-invalid"
                        : ""
                    }`}
                    readOnly
                  />
                  {formik.touched.classStartDate &&
                    formik.errors.classStartDate && (
                      <div className="invalid-feedback">
                        {formik.errors.classStartDate}
                      </div>
                    )}
                </div>
              </div>
              <div className="col-md-6 col-12 mb-2">
                <div className="mb-3">
                  <label className="form-label">
                    Class End Date<span className="text-danger">*</span>
                  </label>
                  <input
                    name="classEndDate"
                    {...formik.getFieldProps("classEndDate")}
                    type="text"
                    className={`form-control  ${
                      formik.touched.classEndDate && formik.errors.classEndDate
                        ? "is-invalid"
                        : ""
                    }`}
                    readOnly
                  />
                  {formik.touched.classEndDate &&
                    formik.errors.classEndDate && (
                      <div className="invalid-feedback">
                        {formik.errors.classEndDate}
                      </div>
                    )}
                </div>
              </div>
              <div className="col-md-6 col-12 mb-2">
                <div className="mb-3">
                  <label className="form-label">
                    Request Date<span className="text-danger">*</span>
                  </label>
                  <input
                    name="requestDate"
                    {...formik.getFieldProps("requestDate")}
                    type="date"
                    className={`form-control  ${
                      formik.touched.requestDate && formik.errors.requestDate
                        ? "is-invalid"
                        : ""
                    }`}
                    
                  />
                  {formik.touched.requestDate && formik.errors.requestDate && (
                    <div className="invalid-feedback">
                      {formik.errors.requestDate}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-md-6 col-12 mb-2">
                <div className="mb-3">
                  <label className="form-label">
                    Withdraw Reason<span className="text-danger">*</span>
                  </label>
                  <select
                    {...formik.getFieldProps("withdrawReason")}
                    name="withdrawReason"
                    className={`form-select  ${
                      formik.touched.withdrawReason &&
                      formik.errors.withdrawReason
                        ? "is-invalid"
                        : ""
                    }`}
                  >
                    <option disabled selected></option>
                    <option value="change day of class">
                      Change day of class
                    </option>
                    <option value="change timing of class">
                      Change timing of class
                    </option>
                  </select>
                  {formik.touched.withdrawReason &&
                    formik.errors.withdrawReason && (
                      <div className="invalid-feedback">
                        {formik.errors.withdrawReason}
                      </div>
                    )}
                </div>
              </div>
              <div className="col-md-6 col-12 mb-2">
                <label className="form-label">Remark Parent</label>
                <div class="input-group mb-3">
                  <textarea
                    name="remarkParent"
                    class="form-control"
                    {...formik.getFieldProps("remarkParent")}
                    id="remarkParent"
                    rows="5"
                  ></textarea>
                </div>
              </div>
              <div className="col-md-6 col-12 mb-2">
                <label className="form-label">Remark Centre</label>
                <span className="text-danger">*</span>
                <div class="input-group mb-3">
                  <textarea
                    name="remarkCenter"
                    className={`form-control  ${
                      formik.touched.remarkCenter &&
                      formik.errors.remarkCenter
                        ? "is-invalid"
                        : ""
                    }`}
                    {...formik.getFieldProps("remarkCenter")}
                    id="remarkCenter"
                    rows="5"
                  ></textarea>
                  {formik.touched.remarkCenter &&
                    formik.errors.remarkCenter && (
                      <div className="invalid-feedback">
                        {formik.errors.remarkCenter}
                      </div>
                    )}
                </div>
              </div>
              <div className="col-md-6 col-12 mb-4">
                <label className="form-label">
                  Last Lesson Date<span className="text-danger">*</span>
                </label>
                <select
                  name="lastLessonDate"
                  {...formik.getFieldProps("lastLessonDate")}
                  onChange={handleLastLessonDateChange} // Updated handler
                  className={`form-select ${
                    formik.touched.lastLessonDate &&
                    formik.errors.lastLessonDate
                      ? "is-invalid"
                      : ""
                  }`}
                >
                  <option value=""></option>
                  {lastLessonDateData &&
                    lastLessonDateData.map((lastLessonDate) => (
                      <option key={lastLessonDate.id} value={lastLessonDate.id}>
                        {lastLessonDate.startDate}
                      </option>
                    ))}
                </select>
                {formik.touched.lastLessonDate &&
                  formik.errors.lastLessonDate && (
                    <div className="invalid-feedback">
                      {formik.errors.lastLessonDate}
                    </div>
                  )}
              </div>
              <div className="col-md-6 col-12 mb-2">
                <div className="mb-3">
                  <label className="form-label">Last paid lesson date</label>
                  <input
                    name="lastPaidLessonDate"
                    {...formik.getFieldProps("lastPaidLessonDate")}
                    type="text"
                    className={`form-control  ${
                      formik.touched.lastPaidLessonDate &&
                      formik.errors.lastPaidLessonDate
                        ? "is-invalid"
                        : ""
                    }`}
                    readOnly
                  />
                </div>
              </div>
              <div className="col-md-6 col-12 mb-2">
                <div className="mb-3">
                  <label className="form-label">No of lesson notice</label>
                  <input
                    name="noOfLessonNotice"
                    {...formik.getFieldProps("noOfLessonNotice")}
                    type="text"
                    className={`form-control  ${
                      formik.touched.noOfLessonNotice &&
                      formik.errors.noOfLessonNotice
                        ? "is-invalid"
                        : ""
                    }`}
                    readOnly
                  />
                  {formik.touched.noOfLessonNotice &&
                    formik.errors.noOfLessonNotice && (
                      <div className="invalid-feedback">
                        {formik.errors.noOfLessonNotice}
                      </div>
                    )}
                </div>
              </div>
              <div className="col-md-6 col-12 mb-2">
                <div className="mb-3">
                  <label className="form-label">
                    No of lesson notice for refund
                  </label>
                  <input
                    name="noOfLessonNoticeForRefund"
                    {...formik.getFieldProps("noOfLessonNoticeForRefund")}
                    type="text"
                    className={`form-control  ${
                      formik.touched.noOfLessonNoticeForRefund &&
                      formik.errors.noOfLessonNoticeForRefund
                        ? "is-invalid"
                        : ""
                    }`}
                    readOnly
                  />
                  {formik.touched.noOfLessonNoticeForRefund &&
                    formik.errors.noOfLessonNoticeForRefund && (
                      <div className="invalid-feedback">
                        {formik.errors.noOfLessonNoticeForRefund}
                      </div>
                    )}
                </div>
              </div>
              <div className="col-md-6 col-12 mb-2">
                <div className="form-check mb-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="depositRefundAllowed"
                    name="depositRefundAllowed"
                    checked={depositRefundAllowed}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      if (isChecked && depositForfeitAllowed) {
                        setDepositForfeit(false);
                        formik.setFieldValue("depositForfeitAllowed", false);
                        formik.setFieldValue("forfeitAmount", ""); // Clear forfeitAmount when switching
                      }
                      setDepositRefund(isChecked);
                      formik.setFieldValue("depositRefundAllowed", isChecked);
                      if (isChecked) {
                        formik.setFieldValue(
                          "refundAmount",
                          formik.values.courseFeesAmount
                        ); // Set refundAmount to courseFeesAmount
                      } else {
                        formik.setFieldValue("refundAmount", ""); // Clear refundAmount when unchecked
                      }
                    }}
                  />
                  <label
                    className="form-check-label"
                    htmlFor="depositRefundAllowed"
                  >
                    Deposit Refund
                  </label>
                </div>
                <div className="form-check mb-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="depositForfeitAllowed"
                    name="depositForfeitAllowed"
                    checked={depositForfeitAllowed}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      if (isChecked && depositRefundAllowed) {
                        setDepositRefund(false);
                        formik.setFieldValue("depositRefundAllowed", false);
                        formik.setFieldValue("refundAmount", ""); // Clear refundAmount when switching
                      }
                      setDepositForfeit(isChecked);
                      formik.setFieldValue("depositForfeitAllowed", isChecked);
                      if (isChecked) {
                        formik.setFieldValue(
                          "forfeitAmount",
                          formik.values.courseFeesAmount
                        ); // Set forfeitAmount to courseFeesAmount
                      } else {
                        formik.setFieldValue("forfeitAmount", ""); // Clear forfeitAmount when unchecked
                      }
                    }}
                  />
                  <label
                    className="form-check-label"
                    htmlFor="depositForfeitAllowed"
                  >
                    Deposit Forfeit
                  </label>
                </div>
                <div className="form-check mb-2">
                  <input
                    type="checkbox"
                    name="courseFeesRefund"
                    id="courseFeesRefund"
                    checked={formik.values.courseFeesRefund}
                    onChange={(e) => {
                      if (!formik.values.lastLessonDate && e.target.checked) {
                        toast.warning("Please select Last Lesson Date first");
                        return;
                      }
                      formik.handleChange(e); // Allow manual unchecking or checking if lastLessonDate exists
                    }}
                    className="form-check-input"
                  />
                  <label
                    className="form-check-label"
                    htmlFor="courseFeesRefund"
                  >
                    Course Fees Refund
                  </label>
                </div>
                <div className="form-check mb-2">
                  <input
                    type="checkbox"
                    name="registrationFeesRefundAllowed"
                    id="registrationFeesRefundAllowed"
                    checked={formik.values.registrationFeesRefundAllowed}
                    onChange={formik.handleChange}
                    className="form-check-input"
                  />
                  <label
                    className="form-check-label"
                    htmlFor="registrationFeesRefundAllowed"
                  >
                    Registration Fees Refund
                  </label>
                </div>
              </div>
              <div className="col-md-6 col-12 mb-2">
                <div className="mb-3">
                  <label className="form-label" htmlFor="expectedRefundDate">
                    Expected Refund Date
                  </label>
                  <input
                    type="date"
                    name="expectedRefundDate"
                    id="expectedRefundDate"
                    value={formik.values.expectedRefundDate}
                    onChange={formik.handleChange}
                    className={`form-control ${
                      formik.touched.expectedRefundDate &&
                      formik.errors.expectedRefundDate
                        ? "is-invalid"
                        : ""
                    }`}
                  />
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-md-12 col-12 mb-3">
                <span class="me-2 fw-medium" style={{ color: "#287f71" }}>
                  Refund Detail
                </span>
              </div>
              <div className="col-md-6 col-12 mb-2">
                <div className="mb-3">
                  <label className="form-label">Refund Name</label>
                  <input
                    name="refundName"
                    {...formik.getFieldProps("refundName")}
                    type="text"
                    className={`form-control  ${
                      formik.touched.refundName && formik.errors.refundName
                        ? "is-invalid"
                        : ""
                    }`}
                  />
                </div>
              </div>
              <div className="col-md-6 col-12 mb-2">
                <div className="mb-3">
                  <label className="form-label">Refund Address</label>
                  <input
                    name="refundAddress"
                    {...formik.getFieldProps("refundAddress")}
                    type="text"
                    className={`form-control  ${
                      formik.touched.refundAddress &&
                      formik.errors.refundAddress
                        ? "is-invalid"
                        : ""
                    }`}
                  />
                </div>
              </div>
              <div className="col-md-6 col-12 mb-2">
                <div className="mb-3">
                  <label className="form-label">Refund Amount</label>
                  <input
                    name="refundAmount"
                    {...formik.getFieldProps("refundAmount")}
                    type="text"
                    className={`form-control  ${
                      formik.touched.refundAmount && formik.errors.refundAmount
                        ? "is-invalid"
                        : ""
                    }`}
                  />
                </div>
              </div>
              <div className="col-md-6 col-12 mb-2">
                <div className="mb-3">
                  <label className="form-label">Refund Mode</label>
                  <select
                    {...formik.getFieldProps("refundMode")}
                    name="refundMode"
                    className={`form-select  ${
                      formik.touched.refundMode && formik.errors.refundMode
                        ? "is-invalid"
                        : ""
                    }`}
                  >
                    <option value="" selected></option>
                    <option value="Cheque">Cheque</option>
                    <option value="Petty Cash">Petty Cash</option>
                    <option value="Pay Now">Pay Now</option>
                    <option value="Bank Transfer">Bank Transfer</option>
                  </select>
                </div>
              </div>
              {formik.values.refundMode === "Cheque" && (
                <>
                  <div className="col-md-6 col-12 mb-2">
                    <label className="form-label">Cheque No</label>
                    <input
                      name="chequeNo"
                      {...formik.getFieldProps("chequeNo")}
                      type="text"
                      className={`form-control ${
                        formik.touched.chequeNo && formik.errors.chequeNo
                          ? "is-invalid"
                          : ""
                      }`}
                    />
                  </div>
                  <div className="col-md-6 col-12 mb-2">
                    <label className="form-label">Cheque Date</label>
                    <input
                      type="date"
                      name="chequeDate"
                      {...formik.getFieldProps("chequeDate")}
                      className={`form-control ${
                        formik.touched.chequeDate && formik.errors.chequeDate
                          ? "is-invalid"
                          : ""
                      }`}
                    />
                  </div>
                  <div className="col-md-6 col-12 mb-2">
                    <label className="form-label">Cheque Amount</label>
                    <input
                      name="chequeAmount"
                      {...formik.getFieldProps("chequeAmount")}
                      type="text"
                      className={`form-control ${
                        formik.touched.chequeAmount &&
                        formik.errors.chequeAmount
                          ? "is-invalid"
                          : ""
                      }`}
                    />
                  </div>
                </>
              )}
              {formik.values.refundMode === "Bank Transfer" && (
                <>
                  <div className="col-md-6 col-12 mb-2">
                    <label className="form-label">Bank Name</label>
                    <input
                      name="bankName"
                      {...formik.getFieldProps("bankName")}
                      type="text"
                      className={`form-control ${
                        formik.touched.bankName && formik.errors.bankName
                          ? "is-invalid"
                          : ""
                      }`}
                    />
                  </div>
                  <div className="col-md-6 col-12 mb-2">
                    <label className="form-label">Bank Account Name</label>
                    <input
                      type="text"
                      name="bankAccountName"
                      {...formik.getFieldProps("bankAccountName")}
                      className={`form-control ${
                        formik.touched.bankAccountName &&
                        formik.errors.bankAccountName
                          ? "is-invalid"
                          : ""
                      }`}
                    />
                  </div>
                  <div className="col-md-6 col-12 mb-2">
                    <label className="form-label">Bank Account Number</label>
                    <input
                      name="bankAccountNumber"
                      {...formik.getFieldProps("bankAccountNumber")}
                      type="text"
                      className={`form-control ${
                        formik.touched.bankAccountNumber &&
                        formik.errors.bankAccountNumber
                          ? "is-invalid"
                          : ""
                      }`}
                    />
                  </div>
                </>
              )}
              <div className="col-md-6 col-12 mb-2">
                <div className="mb-3">
                  <label className="form-label">Forfeit Amount</label>
                  <input
                    name="forfeitAmount"
                    {...formik.getFieldProps("forfeitAmount")}
                    type="text"
                    className={`form-control  ${
                      formik.touched.forfeitAmount &&
                      formik.errors.forfeitAmount
                        ? "is-invalid"
                        : ""
                    }`}
                    readOnly
                  />
                </div>
              </div>
              <div className="overflow-x-auto mb-2">
                <table className="min-w-full border border-gray-300">
                  <tbody>
                    <tr>
                      <td className="border px-4 py-2 font-semibold">
                        Registration Fees
                        <span className="text-danger">*</span>
                      </td>
                      <td className="border px-4 py-2">
                        <input
                          type="number"
                          step="0.01"
                          name="registrationFees"
                          value={formik.values.registrationFees}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          className={`w-full px-2 py-1 border rounded bg-gray-100 ${
                            formik.touched.registrationFees &&
                            formik.errors.registrationFees
                              ? "is-invalid"
                              : ""
                          }`}
                        />
                        {formik.touched.registrationFees &&
                          formik.errors.registrationFees && (
                            <div className="invalid-feedback">
                              {formik.errors.registrationFees}
                            </div>
                          )}
                      </td>
                    </tr>
                    <tr>
                      <td className="border px-4 py-2 font-semibold">
                        Deposit Amount<span className="text-danger">*</span>
                      </td>
                      <td className="border px-4 py-2">
                        <input
                          type="number"
                          step="0.01"
                          name="depositAmount"
                          value={formik.values.depositAmount}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          className={`w-full px-2 py-1 border rounded bg-gray-100 ${
                            formik.touched.depositAmount &&
                            formik.errors.depositAmount
                              ? "is-invalid"
                              : ""
                          }`}
                        />
                        {formik.touched.depositAmount &&
                          formik.errors.depositAmount && (
                            <div className="invalid-feedback">
                              {formik.errors.depositAmount}
                            </div>
                          )}
                      </td>
                    </tr>
                    <tr>
                      <td className="border px-4 py-2 font-semibold">
                        Deposit Offset Amount
                        <span className="text-danger">*</span>
                      </td>
                      <td className="border px-4 py-2">
                        <input
                          type="number"
                          step="0.01"
                          name="depositOffsetAmount"
                          value={formik.values.depositOffsetAmount}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          className={`w-full px-2 py-1 border rounded bg-gray-100 ${
                            formik.touched.depositOffsetAmount &&
                            formik.errors.depositOffsetAmount
                              ? "is-invalid"
                              : ""
                          }`}
                        />
                        {formik.touched.depositOffsetAmount &&
                          formik.errors.depositOffsetAmount && (
                            <div className="invalid-feedback">
                              {formik.errors.depositOffsetAmount}
                            </div>
                          )}
                      </td>
                    </tr>
                    <tr>
                      <td className="border px-4 py-2 font-semibold">
                        No of lesson to refund
                        <span className="text-danger">*</span>
                      </td>
                      <td className="border px-4 py-2">
                        <input
                          type="number"
                          name="noOfLessonToRefund"
                          value={formik.values.noOfLessonToRefund}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          className={`w-full px-2 py-1 border rounded bg-gray-100 ${
                            formik.touched.noOfLessonToRefund &&
                            formik.errors.noOfLessonToRefund
                              ? "is-invalid"
                              : ""
                          }`}
                        />
                        {formik.touched.noOfLessonToRefund &&
                          formik.errors.noOfLessonToRefund && (
                            <div className="invalid-feedback">
                              {formik.errors.noOfLessonToRefund}
                            </div>
                          )}
                      </td>
                    </tr>
                    <tr>
                      <td className="border px-4 py-2 font-semibold">
                        Course Fees Amount<span className="text-danger">*</span>
                      </td>
                      <td className="border px-4 py-2">
                        <input
                          type="number"
                          step="0.01"
                          name="courseFeesAmount"
                          value={formik.values.courseFeesAmount}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          className={`w-full px-2 py-1 border rounded bg-gray-100 ${
                            formik.touched.courseFeesAmount &&
                            formik.errors.courseFeesAmount
                              ? "is-invalid"
                              : ""
                          }`}
                        />
                        {formik.touched.courseFeesAmount &&
                          formik.errors.courseFeesAmount && (
                            <div className="invalid-feedback">
                              {formik.errors.courseFeesAmount}
                            </div>
                          )}
                      </td>
                    </tr>
                    <tr>
                      <td className="border px-4 py-2 font-semibold">
                        Material Fees Amount
                        <span className="text-danger">*</span>
                      </td>
                      <td className="border px-4 py-2">
                        <input
                          type="number"
                          step="0.01"
                          name="materialFeesAmount"
                          value={formik.values.materialFeesAmount}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          className={`w-full px-2 py-1 border rounded bg-gray-100 ${
                            formik.touched.materialFeesAmount &&
                            formik.errors.materialFeesAmount
                              ? "is-invalid"
                              : ""
                          }`}
                        />
                        {formik.touched.materialFeesAmount &&
                          formik.errors.materialFeesAmount && (
                            <div className="invalid-feedback">
                              {formik.errors.materialFeesAmount}
                            </div>
                          )}
                      </td>
                    </tr>
                    <tr>
                      <td className="border px-4 py-2 font-semibold">
                        Total Refundable<span className="text-danger">*</span>
                      </td>
                      <td className="border px-4 py-2">
                        <input
                          type="number"
                          step="0.01"
                          name="totalRefundableAmount"
                          value={formik.values.totalRefundableAmount}
                          readOnly
                          className="w-full px-2 py-1 border rounded bg-gray-200"
                        />
                        {formik.touched.totalRefundableAmount &&
                          formik.errors.totalRefundableAmount && (
                            <div className="invalid-feedback">
                              {formik.errors.totalRefundableAmount}
                            </div>
                          )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

export default StudentWithdrawAdd;
